'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError, type ApiClient } from '@kiao-voice/api-client'
import type {
  EmailVerificationRequestResponse,
  EmailVerificationVerifyResponse,
} from '@kiao-voice/api-contract/email-verification'
import { Alert, Button, Field, Input } from '@kiao-voice/ui-kit'
import { getWebAuthedNoContextClient } from '@/lib/web-api-client'

/**
 * Proving the signed-in account's own email in place — the step every surface
 * shows when an action answers `email_verification_required` (the team invite
 * banner, the campaign claim). The endpoints are account-level, so they go
 * through the no-context client: a stale workspace selection must not 403 them.
 */

/** `POST /auth/email-verification/request` — mails a 6-digit code to the account's address. */
export function requestEmailVerification(
  client: ApiClient = getWebAuthedNoContextClient()
): Promise<EmailVerificationRequestResponse> {
  return client.request<EmailVerificationRequestResponse>(
    '/api/v1/auth/email-verification/request',
    { method: 'POST' }
  )
}

/**
 * `POST /auth/email-verification/verify` — a wrong or expired code answers 422
 * with the OTP `reason` (never 401, which would sign the session out).
 */
export function verifyEmailVerification(
  code: string,
  client: ApiClient = getWebAuthedNoContextClient()
): Promise<EmailVerificationVerifyResponse> {
  return client.request<EmailVerificationVerifyResponse>('/api/v1/auth/email-verification/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

function failureReason(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null
  const body = error.body
  return body && typeof body === 'object' && 'reason' in body
    ? String((body as { reason: unknown }).reason)
    : null
}

/** The `auth.*` key the sign-in form uses for the same OTP failure. */
function verifyErrorKey(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) return 'auth.errorCodeLockout'
  switch (failureReason(error)) {
    case 'expired':
      return 'auth.errorCodeExpired'
    case 'too_many_attempts':
      return 'auth.errorCodeLockout'
    case 'mismatch':
    case 'not_found':
      return 'auth.errorCodeIncorrect'
    default:
      return 'auth.errorCodeFallback'
  }
}

export interface EmailVerificationStepProps {
  /** What is being unlocked and why, shown above the first button. */
  intro: string
  /** The submit button once the code is in, e.g. 「驗證並接受」. */
  submitLabel: string
  /**
   * Called once the code is accepted. The step is over at that point: the
   * caller leaves verification mode and repeats the action it was blocked on,
   * reporting that action's own failures — the code is already spent.
   */
  onVerified: () => void | Promise<void>
  onCancel?: () => void
  /** `data-testid` prefix: `<prefix>-send`, `-code`, `-submit`, `-resend`, `-cancel`. */
  testIdPrefix: string
  requestFn?: () => Promise<unknown>
  verifyFn?: (code: string) => Promise<unknown>
}

export function EmailVerificationStep({
  intro,
  submitLabel,
  onVerified,
  onCancel,
  testIdPrefix,
  requestFn = () => requestEmailVerification(),
  verifyFn = (code) => verifyEmailVerification(code),
}: EmailVerificationStepProps) {
  const { t } = useTranslation('web')
  const codeInputId = useId()
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const sendCode = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await requestFn()
      setCodeSent(true)
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'features/auth/email-verification.tsx' })
      setError(
        t(
          caughtError instanceof ApiError && caughtError.status === 429
            ? 'auth.errorResendRateLimited'
            : 'auth.errorRequestFailed'
        )
      )
    } finally {
      setBusy(false)
    }
  }, [busy, requestFn, t])

  const submitCode = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (busy || code.length !== 6) return
      setBusy(true)
      setError(null)
      try {
        await verifyFn(code)
      } catch (caughtError) {
        reportHandledException(caughtError, { source: 'features/auth/email-verification.tsx' })
        setError(t(verifyErrorKey(caughtError)))
        setBusy(false)
        return
      }
      setBusy(false)
      await onVerified()
    },
    [busy, code, onVerified, t, verifyFn]
  )

  const cancel = onCancel ? (
    <Button
      variant="ghost"
      size="sm"
      onClick={onCancel}
      disabled={busy}
      data-testid={`${testIdPrefix}-cancel`}
    >
      {t('common.cancel')}
    </Button>
  ) : null

  if (!codeSent) {
    return (
      <div className="otp-form-step">
        <p className="note">{intro}</p>
        {error ? <Alert>{error}</Alert> : null}
        <div className="auth-aux">
          <Button
            size="sm"
            onClick={() => void sendCode()}
            disabled={busy}
            data-testid={`${testIdPrefix}-send`}
          >
            {busy ? t('auth.ctaSending') : t('auth.emailVerifySend')}
          </Button>
          {cancel}
        </div>
      </div>
    )
  }

  return (
    <form className="otp-form-step" noValidate onSubmit={(event) => void submitCode(event)}>
      <p className="note" role="status">
        {t('auth.emailVerifyCodeSent')}
      </p>
      <Field label={t('auth.codeLabel')} htmlFor={codeInputId}>
        <Input
          id={codeInputId}
          className="code-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="······"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          data-testid={`${testIdPrefix}-code`}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
            setError(null)
          }}
        />
      </Field>
      {error ? <Alert>{error}</Alert> : null}
      <div className="auth-aux">
        <Button
          size="sm"
          type="submit"
          disabled={busy || code.length !== 6}
          data-testid={`${testIdPrefix}-submit`}
        >
          {busy ? t('auth.ctaVerifying') : submitLabel}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void sendCode()}
          disabled={busy}
          data-testid={`${testIdPrefix}-resend`}
        >
          {t('auth.resendCode')}
        </Button>
        {cancel}
      </div>
    </form>
  )
}
